
import React, { useState } from 'react';
import { Card, Input } from '../components/UI';
import { BrandColor } from '../types';
import { Plus, X, GripVertical, Eye, EyeOff, Trash2 } from 'lucide-react';
import { hexToCmyk } from '../utils/svgHelpers';

interface ColorPaletteProps {
  colors: BrandColor[];
  onChange: (colors: BrandColor[]) => void;
  readOnly?: boolean;
}

export const ColorPalette: React.FC<ColorPaletteProps> = ({ colors, onChange, readOnly }) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const updateColor = (id: string, updates: Partial<BrandColor>) => {
    const updated = colors.map(c => {
      if (c.id === id) {
        // If Hex is changing, auto-recalculate CMYK
        if (updates.hex) {
          return { 
            ...c, 
            ...updates, 
            cmyk: hexToCmyk(updates.hex) // Auto-calc CMYK from new Hex
          };
        }
        // If CMYK is changing, DO NOT touch Hex (allow manual print override)
        return { ...c, ...updates };
      }
      return c;
    });
    onChange(updated);
  };

  const deleteColor = (id: string) => {
    if (!confirm("Remove this color from the palette?")) return;
    onChange(colors.filter(c => c.id !== id));
  };

  const addColor = () => {
    const newColor: BrandColor = {
      id: `c-${Date.now()}`,
      hex: '#000000',
      cmyk: 'C:0 M:0 Y:0 K:100',
      name: 'New Brand Color',
      visible: true
    };
    onChange([...colors, newColor]);
  };

  const toggleVisibility = (id: string) => {
    const updated = colors.map(c => c.id === id ? { ...c, visible: !c.visible } : c);
    onChange(updated);
  };

  // --- DRAG AND DROP ---
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;

    const fromIndex = colors.findIndex(c => c.id === draggedId);
    const toIndex = colors.findIndex(c => c.id === targetId);

    if (fromIndex === -1 || toIndex === -1) return;

    const newColors = [...colors];
    const [moved] = newColors.splice(fromIndex, 1);
    newColors.splice(toIndex, 0, moved);
    
    onChange(newColors);
    setDraggedId(null);
  };

  if (readOnly) return null;

  return (
    <Card className="bg-white/50 backdrop-blur-sm">
      <div className="flex justify-between items-center mb-6">
         <h3 className="font-bold uppercase tracking-widest text-xs text-brand-text/50">Brand Palette</h3>
         <button onClick={addColor} className="text-xs flex items-center gap-1 text-brand-primary font-bold hover:underline">
           <Plus size={14} /> Add Color
         </button>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {colors.map((color) => (
            <div 
              key={color.id} 
              draggable 
              onDragStart={(e) => handleDragStart(e, color.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, color.id)}
              className={`
                 group relative flex flex-col p-3 rounded-2xl bg-white border border-transparent hover:border-brand-text/10 hover:shadow-lg transition-all
                 ${draggedId === color.id ? 'opacity-50 border-dashed border-brand-primary' : ''}
                 ${!color.visible ? 'opacity-60 bg-gray-50' : ''}
              `}
            >
              {/* Drag Handle & Visibility Controls */}
              <div className="absolute top-2 right-2 flex gap-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 rounded-lg p-1">
                 <button onClick={() => toggleVisibility(color.id)} className="p-1 hover:bg-gray-100 rounded-md text-gray-500">
                    {color.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                 </button>
                 <div className="w-px h-4 bg-gray-200"></div>
                 <button onClick={() => deleteColor(color.id)} className="p-1 hover:bg-red-50 rounded-md text-red-400">
                    <Trash2 size={12} />
                 </button>
              </div>

              {/* Color Box (Square) */}
              <div className="relative aspect-square w-full mb-3 group/picker">
                <div 
                   className="w-full h-full rounded-xl shadow-inner border border-black/5"
                   style={{ backgroundColor: color.hex }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/picker:opacity-100 transition-opacity cursor-pointer">
                   <div className="bg-black/20 text-white text-xs font-bold px-2 py-1 rounded backdrop-blur-sm">Edit Hex</div>
                </div>
                <input 
                  type="color" 
                  value={color.hex}
                  onChange={(e) => updateColor(color.id, { hex: e.target.value })}
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                />
              </div>

              {/* Descriptions */}
              <div className="mb-3">
                    <input 
                      className="w-full text-sm font-bold bg-transparent border-b border-transparent hover:border-gray-200 focus:border-brand-primary focus:outline-none transition-colors placeholder:text-gray-300"
                      value={color.name}
                      onChange={(e) => updateColor(color.id, { name: e.target.value })}
                      placeholder="Color Description"
                      title="Name of the color"
                    />
              </div>

              {/* Hex & CMYK Inputs - Stacked Full Width */}
              <div className="space-y-2 mt-auto">
                 <div className="bg-gray-50 rounded p-1.5 w-full">
                    <label className="text-[8px] font-bold text-gray-400 uppercase block mb-0.5">HEX</label>
                    <input 
                       className="w-full text-[10px] font-mono bg-transparent outline-none uppercase text-gray-700"
                       value={color.hex}
                       onChange={(e) => updateColor(color.id, { hex: e.target.value })}
                    />
                 </div>
                 <div className="bg-gray-50 rounded p-1.5 w-full">
                    <label className="text-[8px] font-bold text-gray-400 uppercase block mb-0.5">CMYK</label>
                    <input 
                       className="w-full text-[10px] font-mono bg-transparent outline-none uppercase text-gray-700"
                       value={color.cmyk || hexToCmyk(color.hex)}
                       onChange={(e) => updateColor(color.id, { cmyk: e.target.value })}
                       placeholder="C:0 M:0..."
                       title="Edit CMYK manually (Does not change Hex)"
                    />
                 </div>
              </div>

              {/* Grip Indicator */}
              <div className="absolute top-2 left-2 text-gray-300 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100">
                 <GripVertical size={14} />
              </div>

            </div>
        ))}
      </div>
    </Card>
  );
};
